import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  CheckNotes,
  InspectionFieldDto,
  TextBank
} from '@authority-labs/api-interfaces';
import { nameof } from '@authority-labs/helpers';
import { TranslocoService } from '@ngneat/transloco';
import { TextBankService } from '@check-list/app/shared';
import { NotesBankDialogService } from '@check-list/app/shared/dialogs/notes-bank-dialog/notes-bank-dialog.service';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { Observable, Subject } from 'rxjs';
import { map, shareReplay, takeUntil } from 'rxjs/operators';
import { InspectionDataService } from '../../inspection-data.service';
import { InspectionsService } from '../../inspections.service';
import { DraftStateService } from '../../inspection-draft/services/draft-state.service';
import {
  encodeInkNote,
  isInkNote,
  decodeInkNote
} from '../../inspection-draft/models/draft-form.model';
import { generateSvgPath } from '../../inspection-draft/utils/svg-path.util';

@Component({
  selector: 'authority-labs-check-notes',
  templateUrl: './check-notes.component.html',
  styleUrls: ['./check-notes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CheckNotesComponent implements OnInit, OnDestroy {
  @Input() notes: CheckNotes[] = [];
  @Input() type: string;
  @Input() readonly: boolean;
  @Input() printPreview: boolean;
  @Input() fieldSection: string;
  @Input() fieldTitle: string;
  @Output() dataChange: EventEmitter<CheckNotes[]> = new EventEmitter();

  inspectionField: InspectionFieldDto | null;
  filteredOptions: Observable<string[]>;
  notesBank: string[] = [];
  notesBank$: Observable<string[]>;
  inputControl = new UntypedFormControl();
  editableNoteIndex: number | null = null;
  qb: RequestQueryBuilder;

  // State for AI professionalization
  aiProcessing = false;
  aiProcessedText: string;
  originalTexts = new Map<number, string>();
  confirmVisibleNotePopupIndex: number | null = null;

  // Ink helpers exposed to template
  isInkNote = isInkNote;
  decodeInkNote = decodeInkNote;
  generateSvgPath = generateSvgPath;

  private componentDestroyed$: Subject<boolean> = new Subject();

  get inkMode(): boolean {
    return !!this.inkState;
  }

  get noteInputFieldId(): string {
    return this.fieldSection + ':' + this.fieldTitle + ':noteInput';
  }

  get hasInkStrokes(): boolean {
    return this.inkState?.getStrokes(this.noteInputFieldId)?.length > 0;
  }

  constructor(
    private readonly notesBankDialogService: NotesBankDialogService,
    private readonly textBankService: TextBankService,
    private readonly inspectionsService: InspectionsService,
    private readonly snackBar: MatSnackBar,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly inspectionDataService: InspectionDataService,
    private readonly translocoService: TranslocoService,
    @Optional() @Inject(DraftStateService) private inkState: DraftStateService
  ) {}

  ngOnInit(): void {
    this.qb = RequestQueryBuilder.create();
    this.qb.search({
      [nameof<TextBank>('type')]: {
        $eq: this.type
      }
    });

    this.notesBank$ = this.textBankService.getAll(this.qb).pipe(
      takeUntil(this.componentDestroyed$),
      map((resp) => resp.map((item) => item.content))
    );

    this.filteredOptions = this.inputControl.valueChanges.pipe(
      // filter((value: string) => value && value.length > 2),
      map((value) => this.filter(value))
    );

    this.inspectionDataService.$currentStandardDescription.subscribe(
      (standard) => {
        if (standard) {
          this.inspectionField = {
            standard,
            fieldSection: this.fieldSection,
            fieldTitle: this.fieldTitle
          };
        }
      }
    );

    // In ink mode, re-check "add" button when strokes change on the noteInput field
    if (this.inkState) {
      this.inkState
        .getStrokes$(this.noteInputFieldId)
        .pipe(takeUntil(this.componentDestroyed$))
        .subscribe(() => this.changeDetectorRef.markForCheck());
    }
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }

  onChange(): void {
    this.dataChange.emit(this.notes);
  }

  onFocus(): void {
    this.notesBank$.pipe(shareReplay(1)).subscribe((resp) => {
      this.notesBank = resp;
      this.inputControl.setValue(this.inputControl.value || '');
    });
  }

  addNote(e: Event): void {
    e.stopPropagation();
    this.notes = this.notes ?? [];
    this.notes.push({
      addNotesToReport: true,
      notes: this.inputControl.value
    });
    this.inputControl.setValue('');
    this.onChange();
  }

  addInkNote(): void {
    if (!this.hasInkStrokes) return;
    this.notes = this.notes ?? [];
    const strokes = this.inkState.getStrokes(this.noteInputFieldId);
    this.notes.push({
      addNotesToReport: true,
      notes: encodeInkNote(strokes)
    });
    this.inkState.clearField(this.noteInputFieldId);
    this.onChange();
  }

  clearInkField(): void {
    this.inkState.clearField(this.noteInputFieldId);
    this.changeDetectorRef.markForCheck();
  }

  removeNote(noteIndex: number): void {
    this.notes.splice(noteIndex, 1);
    this.onChange();
  }

  editNote(noteIndex: number): void {
    this.editableNoteIndex = noteIndex;
  }

  applyChanges(): void {
    this.editableNoteIndex = null;
    this.onChange();
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.notes, event.previousIndex, event.currentIndex);
    this.onChange();
  }

  showNotesBank(e: Event) {
    e.stopPropagation();
    this.notesBankDialogService
      .showMulti(this.type)
      .afterClosed()
      .subscribe((selected) => {
        if (selected) {
          if (Array.isArray(selected)) {
            this.addMultipleNotes(selected);
          } else {
            this.inputControl.setValue(selected);
          }
        }
        this.notesBank$.subscribe((resp) => (this.notesBank = resp));
      });
  }

  confirm(index: number): void {
    if (index === -1) {
      this.inputControl.setValue(this.aiProcessedText);
    } else {
      this.notes[this.confirmVisibleNotePopupIndex].notes =
        this.aiProcessedText;
    }
    this.cancel();
    this.changeDetectorRef.markForCheck();
  }

  cancel(): void {
    this.confirmVisibleNotePopupIndex = null;
    this.aiProcessedText = null;
  }

  onPopoverVisibleChange(visible: boolean): void {
    if (!visible) {
      this.cancel();
    }
  }

  private filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.notesBank.filter((note) =>
      note.toLowerCase().includes(filterValue)
    );
  }

  private addMultipleNotes(notes: string[] = []): void {
    notes.forEach((item) => {
      this.notes.push({
        addNotesToReport: true,
        notes: item
      });
    });
    this.onChange();
  }

  // Method to professionalize input field text
  professionalizeInput(): void {
    if (!this.inputControl.value || !this.inspectionField) {
      return;
    }

    this.aiProcessing = true;
    const originalText = this.inputControl.value;

    const dto: InspectionFieldDto = {
      ...this.inspectionField,
      userInput: originalText
    };

    this.inspectionsService
      .professionalize(dto)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (result) => {
          this.aiProcessedText = result;
          this.originalTexts.set(-1, originalText); // -1 for input field
          this.aiProcessing = false;
          this.confirmVisibleNotePopupIndex = -1;
          this.changeDetectorRef.markForCheck();
        },
        error: (err) => {
          console.error('AI professionalization failed:', err);
          this.aiProcessing = false;
          this.snackBar.open(
            this.translocoService.translate(
              'inspection.fields.case:aiRewriteError'
            ),
            this.translocoService.translate('button.close'),
            { duration: 5000 }
          );
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  // Method to undo input professionalization
  undoInputProfessionalization(): void {
    const original = this.originalTexts.get(-1);
    if (original !== undefined) {
      this.inputControl.setValue(original);
      this.originalTexts.delete(-1);
      this.changeDetectorRef.markForCheck();
    }
  }

  // Method to professionalize existing note
  professionalizeNote(index: number): void {
    if (!this.notes[index]?.notes || !this.inspectionField) {
      return;
    }

    this.aiProcessing = true;
    const originalText = this.notes[index].notes;

    const dto: InspectionFieldDto = {
      ...this.inspectionField,
      userInput: originalText
    };

    this.inspectionsService
      .professionalize(dto)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (result) => {
          this.aiProcessedText = result;
          this.originalTexts.set(index, originalText);
          this.aiProcessing = false;
          this.confirmVisibleNotePopupIndex = index;
          this.changeDetectorRef.markForCheck();
          this.onChange();
        },
        error: (err) => {
          console.error('AI professionalization failed:', err);
          this.aiProcessing = false;
          this.snackBar.open(
            this.translocoService.translate(
              'inspection.fields.case:aiRewriteError'
            ),
            this.translocoService.translate('button.close'),
            { duration: 5000 }
          );
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  // Method to undo note professionalization
  undoNoteProfessionalization(index: number): void {
    const original = this.originalTexts.get(index);
    if (original !== undefined) {
      this.notes[index].notes = original;
      this.originalTexts.delete(index);
      this.onChange();
      this.changeDetectorRef.markForCheck();
    }
  }

  // Helper to check if note has undo available
  hasUndoForNote(index: number): boolean {
    return this.originalTexts.has(index);
  }

  // Helper to check if input has undo available
  hasUndoForInput(): boolean {
    return this.originalTexts.has(-1);
  }
}
